import { Tooltip } from "@mui/material";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import styles from "../StructuredPromptImageCanvas.module.scss";

interface ObjectKeypointsProps {
	keypoints: Array<{ name: string; x: number; y: number }>;
	canvasSize: { width: number; height: number };
	hoveredKeypointName?: string | null;
	onKeypointMove: (name: string, x: number, y: number) => void;
}

type DragState = {
	name: string;
	startClientX: number;
	startClientY: number;
	startKpX: number;
	startKpY: number;
	currentX: number;
	currentY: number;
};

const DOT_SIZE = 8;

export const ObjectKeypoints = ({
	keypoints,
	canvasSize,
	hoveredKeypointName,
	onKeypointMove,
}: ObjectKeypointsProps) => {
	const [drag, setDrag] = useState<DragState | null>(null);
	// Ref so event handlers always read the latest drag state without stale closures
	const dragRef = useRef<DragState | null>(null);
	dragRef.current = drag;

	useEffect(() => {
		if (!drag) return;

		const onMouseMove = (e: MouseEvent) => {
			const d = dragRef.current;
			if (!d) return;
			const deltaX = ((e.clientX - d.startClientX) / canvasSize.width) * 100;
			const deltaY = ((e.clientY - d.startClientY) / canvasSize.height) * 100;
			const newX = Math.max(0, Math.min(100, d.startKpX + deltaX));
			const newY = Math.max(0, Math.min(100, d.startKpY + deltaY));
			setDrag((prev) => (prev ? { ...prev, currentX: newX, currentY: newY } : null));
		};

		const onMouseUp = () => {
			const d = dragRef.current;
			if (d) onKeypointMove(d.name, d.currentX, d.currentY);
			setDrag(null);
		};

		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		return () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!drag, canvasSize.width, canvasSize.height, onKeypointMove]);

	return (
		<>
			{keypoints.map((kp) => {
				const isDragging = drag?.name === kp.name;
				const kpX = isDragging ? drag!.currentX : kp.x;
				const kpY = isDragging ? drag!.currentY : kp.y;
				const left = (kpX / 100) * canvasSize.width - DOT_SIZE / 2;
				const top = (kpY / 100) * canvasSize.height - DOT_SIZE / 2;
				const isHighlighted = hoveredKeypointName === kp.name || isDragging;

				return (
					<Tooltip key={kp.name} title={isDragging ? "" : kp.name} placement="top" arrow>
						<div
							className={clsx(styles.keypointDot, {
								[styles.keypointDotHighlighted]: isHighlighted,
								[styles.keypointDotDragging]: isDragging,
							})}
							style={{ left, top, width: DOT_SIZE, height: DOT_SIZE }}
							onMouseDown={(e) => {
								e.stopPropagation();
								e.preventDefault();
								setDrag({
									name: kp.name,
									startClientX: e.clientX,
									startClientY: e.clientY,
									startKpX: kp.x,
									startKpY: kp.y,
									currentX: kp.x,
									currentY: kp.y,
								});
							}}
						/>
					</Tooltip>
				);
			})}
		</>
	);
};
